import { ReactNode, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, Eye, EyeOff, TrendingUp } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";
import { GoogleCredentialResponse, GoogleLogin } from "@react-oauth/google";

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const {
    isAuthenticated,
    loading,
    login,
    loginWithGoogle,
    error: authError,
  } = useAuthStore();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});

    const result = loginSchema.safeParse({ email, password });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((error) => {
        if (error.path[0]) {
          fieldErrors[error.path[0] as string] = error.message;
        }
      });
      setFormErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    try {
      await login({ email, password });
      toast({
        title: "Login successful!",
        description: "Welcome back to RadarTicker",
      });
      // Reload current page to fetch protected data
      window.location.reload();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Invalid email or password";
      toast({
        title: "Login Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const googleLoginSuccess = async (
    googleResponse: GoogleCredentialResponse,
  ) => {
    if (googleResponse.credential) {
      const response = await loginWithGoogle(googleResponse.credential);
      if (response) {
        window.location.reload();
      }
    } else {
      toast({
        title: "Google Login Failed",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    }
  };

  const googleLoginFailure = () => {
    toast({
      title: "Google Login Failed",
      description: "Something went wrong. Please try again.",
      variant: "destructive",
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <Dialog open onOpenChange={() => navigate("/")}>
          <DialogContent
            className="sm:max-w-md max-h-[90vh] overflow-y-auto backdrop-blur-sm"
            onPointerDownOutside={(e) => e.preventDefault()}
            style={{}}
          >
            <DialogHeader className="text-center sm:text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <TrendingUp className="w-6 h-6 text-success" />
                <span className="text-lg font-bold">RadarTicker</span>
              </div>
              <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Lock className="h-5 w-5 text-primary" />
              </div>
              <DialogTitle className="text-xl">Login Required</DialogTitle>
              <DialogDescription>Sign in to access this page</DialogDescription>
            </DialogHeader>

            {/* Login Form */}
            <form onSubmit={handleLogin} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label htmlFor="protected-email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="protected-email"
                    type="email"
                    placeholder="m@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`pl-9 ${formErrors.email ? "border-destructive" : ""}`}
                  />
                </div>
                {formErrors.email && (
                  <p className="text-xs text-destructive">{formErrors.email}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="protected-password">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs text-primary hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="protected-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`pl-9 pr-9 ${formErrors.password ? "border-destructive" : ""}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {formErrors.password && (
                  <p className="text-xs text-destructive">
                    {formErrors.password}
                  </p>
                )}
                {authError && (
                  <p className="text-xs text-destructive mt-1">
                    {authError?.toString()}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                variant="cta"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Signing in...
                  </div>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>

            {/* Google Login */}
            <div className="flex justify-center">
              <GoogleLogin
                text="continue_with"
                containerProps={{
                  style: {
                    width: "100%",
                    margin: "auto",
                    backgroundColor: "transparent",
                  },
                }}
                onSuccess={googleLoginSuccess}
                // @ts-expect-error onFailure is not in the published prop types
                onFailure={googleLoginFailure}
              />
            </div>

            {/* Sign up & go home */}
            <div className="space-y-2 pt-1">
              <p className="text-center text-sm text-muted-foreground">
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  className="text-primary font-semibold hover:underline"
                >
                  Sign up
                </Link>
              </p>
              <Button
                variant="ghost"
                className="w-full text-muted-foreground text-sm"
                onClick={() => navigate("/")}
              >
                Go back home
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
